import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Chip, CircularProgress,
  Alert, Snackbar, Accordion, AccordionSummary, AccordionDetails,
  Switch, FormControlLabel, IconButton, Grid, Avatar, Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SaveIcon from '@mui/icons-material/Save';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import PetsIcon from '@mui/icons-material/Pets';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ImageIcon from '@mui/icons-material/Image';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp, addDoc, collection, writeBatch, arrayUnion } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { jsPDF } from 'jspdf';
import { db, storage } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';

import ImageViewerDialog from '../components/imaging/ImageViewerDialog';

import { COLORS } from '../theme/constants';
import { IMAGING_STATUS_CONFIG, MODALITY_CONFIG, calcAge } from '../models/imagingModel';

const { primary: IMG_PRIMARY, teal: IMG_TEAL, danger: DANGER, success: SUCCESS, info: INFO } = COLORS;

const CLINIC_NAME = 'Hospital Clínico Veterinario UDLA';
const C = { primary: [26, 58, 92], teal: [44, 184, 160], text: [30, 30, 30], muted: [100, 110, 120], light: [244, 246, 248], border: [220, 225, 230], white: [255, 255, 255] };

export default function ImagingReport() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { currentUser, userData } = useAuth();
  const { activeBranch } = useBranch();

  const [order, setOrder] = useState(null);
  const [existingResult, setExistingResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [findings, setFindings] = useState([]);
  const [overallImpression, setOverallImpression] = useState('');
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [reportFiles, setReportFiles] = useState([]);
  const [uploadingReport, setUploadingReport] = useState(false);

  // Una orden aprobada (o ya entregada) no admite más edición ni re-firma
  const isApproved = ['approved', 'reported', 'delivered'].includes(order?.status);

  useEffect(() => {
    const loadData = async () => {
      try {
        const orderSnap = await getDoc(doc(db, 'imagingOrders', id));
        if (!orderSnap.exists()) {
          setSnackbar({ open: true, message: 'Orden no encontrada', severity: 'error' });
          return;
        }

        const orderData = { id: orderSnap.id, ...orderSnap.data() };
        setOrder(orderData);

        // Initialize findings from studies
        setFindings(orderData.studiesRequested?.map(study => ({
          examTypeId: study.examTypeId,
          examTypeName: study.examTypeName,
          modality: study.modality,
          bodyRegion: study.bodyRegion,
          technique: '',
          findings: '',
          impression: '',
          recommendation: '',
          criticalFinding: false,
          criticalFindingText: '',
          measurements: study.modality === 'ECO' ? [] : null,
        })) || []);

        // Check for existing result
        if (orderData.resultId) {
          const resultSnap = await getDoc(doc(db, 'imagingResults', orderData.resultId));
          if (resultSnap.exists()) {
            const resultData = { id: resultSnap.id, ...resultSnap.data() };
            setExistingResult(resultData);
            setFindings(resultData.findings || []);
            setOverallImpression(resultData.overallImpression || '');
            const existingFiles = resultData.reportFiles || (resultData.reportFile ? [resultData.reportFile] : []);
            setReportFiles(existingFiles);
          }
        }
      } catch (err) {
        console.error('Error loading data:', err);
        setSnackbar({ open: true, message: `Error: ${err.message}`, severity: 'error' });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  const updateFinding = (index, field, value) => {
    setFindings(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addMeasurement = (findingIndex) => {
    setFindings(prev => {
      const updated = [...prev];
      updated[findingIndex].measurements = [...(updated[findingIndex].measurements || []), { structure: '', value: '', normal: true }];
      return updated;
    });
  };

  const updateMeasurement = (findingIndex, measurementIndex, field, value) => {
    setFindings(prev => {
      const updated = [...prev];
      updated[findingIndex].measurements[measurementIndex] = {
        ...updated[findingIndex].measurements[measurementIndex],
        [field]: value
      };
      return updated;
    });
  };

  const removeMeasurement = (findingIndex, measurementIndex) => {
    setFindings(prev => {
      const updated = [...prev];
      updated[findingIndex].measurements = updated[findingIndex].measurements.filter((_, i) => i !== measurementIndex);
      return updated;
    });
  };

  const handleReportUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setSnackbar({ open: true, message: 'Solo se permiten archivos PDF', severity: 'error' });
      return;
    }
    setUploadingReport(true);
    try {
      const storageRef = ref(storage, `imagingReports/${order.id}/${Date.now()}_${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);
      const downloadURL = await new Promise((resolve, reject) => {
        uploadTask.on('state_changed', null, reject, async () => {
          resolve(await getDownloadURL(uploadTask.snapshot.ref));
        });
      });
      setReportFiles(prev => [...prev, {
        fileName: file.name,
        downloadURL,
        fileType: file.type,
        path: storageRef.fullPath,
        uploadedAt: new Date().toISOString(),
        uploadedBy: currentUser?.uid,
        uploadedByName: userData?.name || userData?.email || '',
      }]);
      setSnackbar({ open: true, message: 'PDF adjunto correctamente', severity: 'success' });
    } catch (err) {
      console.error('Upload error:', err);
      setSnackbar({ open: true, message: `Error al subir PDF: ${err.message}`, severity: 'error' });
    } finally {
      setUploadingReport(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!order) return;
    if (isApproved) {
      setSnackbar({ open: true, message: 'El informe ya fue aprobado; no admite modificaciones', severity: 'info' });
      return;
    }

    setSaving(true);
    try {
      const batch = writeBatch(db);

      // Create or update result
      const existingResultId = existingResult?.id || order.resultId;
      const resultRef = existingResultId
        ? doc(db, 'imagingResults', existingResultId)
        : doc(collection(db, 'imagingResults'));

      batch.set(resultRef, {
        orderId: order.id,
        orderNumber: order.orderNumber,
        patientId: order.patientId,
        patientName: order.patientName,
        patientSpecies: order.patientSpecies,
        radiologistId: currentUser?.uid || '',
        radiologistName: userData?.name || '',
        findings,
        overallImpression,
        reportFiles,
        radiologistSignature: `${userData?.name || ''} | Mat. ${userData?.matricula || ''}`,
        approvedAt: null,
        reportedAt: null,
        createdAt: existingResult?.createdAt || serverTimestamp(),
        updatedAt: serverTimestamp(),
      }, { merge: true });

      // Update order status to draft_report
      batch.update(doc(db, 'imagingOrders', order.id), {
        resultId: resultRef.id,
        status: 'draft_report',
        updatedAt: serverTimestamp(),
      });

      await batch.commit();

      // Audit log
      await addDoc(collection(db, 'imagingOrders', order.id, 'auditLog'), {
        action: 'report_saved',
        details: 'Informe guardado como borrador',
        performedBy: currentUser?.uid,
        performedByName: userData?.name || userData?.email,
        timestamp: serverTimestamp(),
      });

      setSnackbar({ open: true, message: 'Borrador guardado', severity: 'success' });
    } catch (err) {
      console.error('Save error:', err);
      setSnackbar({ open: true, message: `Error: ${err.message}`, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    if (!order) return;
    if (isApproved) {
      setSnackbar({ open: true, message: 'Este informe ya fue aprobado y firmado', severity: 'info' });
      return;
    }
    setSaving(true);
    try {
      const batch = writeBatch(db);

      // Create or update result
      const existingResultId = existingResult?.id || order.resultId;
      const resultRef = existingResultId
        ? doc(db, 'imagingResults', existingResultId)
        : doc(collection(db, 'imagingResults'));

      batch.set(resultRef, {
        orderId: order.id,
        orderNumber: order.orderNumber,
        patientId: order.patientId,
        patientName: order.patientName,
        patientSpecies: order.patientSpecies,
        radiologistId: currentUser?.uid || '',
        radiologistName: userData?.name || '',
        findings,
        overallImpression,
        reportFiles,
        radiologistSignature: `${userData?.name || ''} | Mat. ${userData?.matricula || ''}`,
        reportedAt: serverTimestamp(),
        approvedAt: null,
        createdAt: existingResult?.createdAt || serverTimestamp(),
        updatedAt: serverTimestamp(),
      }, { merge: true });

      // Update order status to reported (Informado)
      batch.update(doc(db, 'imagingOrders', order.id), {
        resultId: resultRef.id,
        status: 'reported',
        statusHistory: arrayUnion({
          status: 'reported',
          changedAt: new Date(),
          changedBy: currentUser?.uid,
          changedByName: userData?.name || userData?.email,
          notes: 'Informe reportado',
        }),
        reportGeneratedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Check for critical findings
      const criticalFindings = findings.filter(f => f.criticalFinding);
      if (criticalFindings.length > 0) {
        const alertRef = doc(collection(db, 'imagingCriticalAlerts'));
        batch.set(alertRef, {
          orderId: order.id,
          orderNumber: order.orderNumber,
          resultId: resultRef.id,
          patientId: order.patientId,
          patientName: order.patientName,
          vetId: order.vetId,
          vetName: order.vetName,
          branchId: order.branchId,
          criticalFindings: criticalFindings.map(f => ({
            examTypeName: f.examTypeName,
            finding: f.criticalFindingText,
          })),
          isResolved: false,
          createdAt: serverTimestamp(),
        });
      }

      await batch.commit();

      // Audit log
      await addDoc(collection(db, 'imagingOrders', order.id, 'auditLog'), {
        action: 'report_reported',
        details: 'Informe de imagen reportado',
        performedBy: currentUser?.uid,
        performedByName: userData?.name || userData?.email,
        timestamp: serverTimestamp(),
      });

      // Generate PDF
      await generateImagingReportPDF(order, resultRef.id, { findings, overallImpression }, activeBranch);

      setSnackbar({ open: true, message: 'Informe aprobado y PDF generado', severity: 'success' });
      navigate(`/imaging/orders/${order.id}`);
    } catch (err) {
      console.error('Approve error:', err);
      setSnackbar({ open: true, message: `Error: ${err.message}`, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const generateImagingReportPDF = async (order, resultId, reportData, branch = {}) => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pw = 210;
    const margin = 18;
    const cw = pw - margin * 2;
    let y = margin;

    const sf = (arr) => doc.setFillColor(...arr);
    const sc = (arr) => doc.setTextColor(...arr);
    const sd = (arr) => doc.setDrawColor(...arr);
    const branchName = branch.name || CLINIC_NAME;
    const contactLine = [branch.address, branch.phone && `Tel: ${branch.phone}`].filter(Boolean).join('  ·  ');

    // Header - simple, no background
    doc.setFont('helvetica', 'bold'); doc.setFontSize(16); sc(C.text);
    doc.text(branchName, margin, 12);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10); sc(C.muted);
    doc.text('INFORME DE DIAGNÓSTICO POR IMÁGENES', margin, 20);
    if (branch.website) { doc.setFontSize(8); doc.text(branch.website, margin, 25); }
    if (contactLine) { doc.setFontSize(8); doc.text(contactLine, 210 - margin, 25, { align: 'right' }); }
    doc.setFontSize(9);
    const dateStr = new Date().toLocaleDateString('es-CL');
    doc.text(`N° ${order.orderNumber || '—'}   |   Fecha: ${dateStr}`, pw - margin, 12, { align: 'right' });

    y = 28;

    // Patient card - simple border, no background
    sd(C.border); doc.setLineWidth(0.5);
    doc.rect(margin, y, cw, 30, 'D');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9); sc(C.text);
    doc.text('DATOS DEL PACIENTE', margin + 4, y + 6);
    sd(C.border); doc.setLineWidth(0.3);
    doc.line(margin + 4, y + 8, margin + cw / 2 - 4, y + 8);

    doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); sc(C.text);
    const col1 = margin + 4;
    const col2 = margin + cw / 2 + 4;
    const lh = 6;
    let ly = y + 14;
    doc.setFont('helvetica', 'bold'); sc(C.muted); doc.text('Nombre:', col1, ly); doc.setFont('helvetica', 'normal'); sc(C.text); doc.text(order.patientName || '—', col1 + 20, ly);
    doc.setFont('helvetica', 'bold'); sc(C.muted); doc.text('Especie:', col2, ly); doc.setFont('helvetica', 'normal'); sc(C.text); doc.text(order.patientSpecies || '—', col2 + 20, ly);
    ly += lh;
    doc.setFont('helvetica', 'bold'); sc(C.muted); doc.text('Raza:', col1, ly); doc.setFont('helvetica', 'normal'); sc(C.text); doc.text(order.patientBreed || '—', col1 + 20, ly);
    doc.setFont('helvetica', 'bold'); sc(C.muted); doc.text('Peso:', col2, ly); doc.setFont('helvetica', 'normal'); sc(C.text); doc.text(`${order.patientWeight || '—'} kg`, col2 + 16, ly);
    ly += lh;
    const age = calcAge(order.patientBirthdate);
    doc.setFont('helvetica', 'bold'); sc(C.muted); doc.text('Edad:', col1, ly); doc.setFont('helvetica', 'normal'); sc(C.text); doc.text(age || '—', col1 + 16, ly);
    doc.setFont('helvetica', 'bold'); sc(C.muted); doc.text('Propietario:', col2, ly); doc.setFont('helvetica', 'normal'); sc(C.text); doc.text(order.ownerName || '—', col2 + 28, ly);

    y += 36;

    // Studies
    reportData.findings.forEach((finding, idx) => {
      if (y > 240) { doc.addPage(); y = margin; }

      // Study header
      doc.setFont('helvetica', 'bold'); doc.setFontSize(10); sc(C.text);
      doc.text(`${idx + 1}. ${finding.examTypeName}`, margin, y);
      sd(C.border); doc.setLineWidth(0.5);
      doc.line(margin, y + 2, margin + 60, y + 2);
      y += 7;

      // Modality chip
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8); sc(C.muted);
      doc.text(`Modalidad: ${finding.modality}`, margin, y);
      y += 6;

      // Technique
      if (finding.technique) {
        doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); sc(C.text);
        doc.text('TÉCNICA:', margin, y);
        y += 4;
        doc.setFont('helvetica', 'normal'); sc(C.text);
        const lines = doc.splitTextToSize(finding.technique, cw);
        doc.text(lines, margin, y);
        y += lines.length * 4 + 4;
      }

      // Findings
      if (finding.findings) {
        doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); sc(C.text);
        doc.text('HALLAZGOS:', margin, y);
        y += 4;
        doc.setFont('helvetica', 'normal'); sc(C.text);
        const lines = doc.splitTextToSize(finding.findings, cw);
        doc.text(lines, margin, y);
        y += lines.length * 4 + 4;
      }

      // Measurements for ECO
      if (finding.measurements && finding.measurements.length > 0) {
        doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); sc(C.text);
        doc.text('MEDICIONES:', margin, y);
        y += 4;
        finding.measurements.forEach((m) => {
          doc.setFont('helvetica', 'normal'); sc(C.text);
          doc.text(`${m.structure}: ${m.value} ${m.normal ? '(Normal)' : '(Anormal)'}`, margin + 4, y);
          y += 4;
        });
        y += 2;
      }

      // Impression
      if (finding.impression) {
        doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); sc(C.text);
        doc.text('IMPRESIÓN DIAGNÓSTICA:', margin, y);
        y += 4;
        doc.setFont('helvetica', 'normal'); sc(C.text);
        const lines = doc.splitTextToSize(finding.impression, cw);
        doc.text(lines, margin, y);
        y += lines.length * 4 + 4;
      }

      // Critical finding - simple border
      if (finding.criticalFinding && finding.criticalFindingText) {
        sd([220, 38, 38]); doc.setLineWidth(0.5);
        doc.rect(margin, y, cw, 12, 'D');
        doc.setFont('helvetica', 'bold'); doc.setFontSize(8); sc([220, 38, 38]);
        doc.text('⚠ HALLAZGO CRÍTICO', margin + 4, y + 4);
        doc.setFont('helvetica', 'normal'); sc(C.text);
        const lines = doc.splitTextToSize(finding.criticalFindingText, cw - 8);
        doc.text(lines, margin + 4, y + 8);
        y += 14;
      }

      // Recommendation
      if (finding.recommendation) {
        doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); sc(C.text);
        doc.text('RECOMENDACIONES:', margin, y);
        y += 4;
        doc.setFont('helvetica', 'normal'); sc(C.text);
        const lines = doc.splitTextToSize(finding.recommendation, cw);
        doc.text(lines, margin, y);
        y += lines.length * 4 + 6;
      }

      y += 4;
    });

    // Overall impression
    if (reportData.overallImpression && reportData.findings.length > 1) {
      if (y > 240) { doc.addPage(); y = margin; }
      doc.setFont('helvetica', 'bold'); doc.setFontSize(9); sc(C.text);
      doc.text('IMPRESIÓN GENERAL', margin, y);
      sd(C.border); doc.setLineWidth(0.5);
      doc.line(margin, y + 2, margin + 50, y + 2);
      y += 6;
      doc.setFont('helvetica', 'normal'); sc(C.text);
      const lines = doc.splitTextToSize(reportData.overallImpression, cw);
      doc.text(lines, margin, y);
      y += lines.length * 4 + 6;
    }

    // Signature
    if (y > 250) { doc.addPage(); y = margin; }
    y = Math.max(y, 260);
    sd(C.border); doc.setLineWidth(0.3);
    doc.line(margin, y, margin + 70, y);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); sc(C.muted);
    doc.text('Firma del Radiólogo / Médico Veterinario', margin + 2, y + 5);
    doc.setFont('helvetica', 'bold'); sc(C.text);
    doc.text(userData?.name || '—', margin + 2, y + 10);
    doc.setFont('helvetica', 'normal'); sc(C.muted);
    doc.text(`Mat. Prof.: ${userData?.matricula || '—'}`, margin + 2, y + 14);

    // Footer
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      sf(C.primary); doc.rect(0, 287, pw, 10, 'F');
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7); sc(C.white);
      doc.text(`${CLINIC_NAME} — Sistema de Gestión Hospitalaria Sphere`, margin, 292);
      doc.text(`Página ${i} de ${totalPages}`, pw - margin, 292, { align: 'right' });
    }

    doc.save(`informe-imagen-${order.orderNumber || 'temp'}.pdf`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!order) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Orden no encontrada</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#F4F6F8', minHeight: '100vh', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button onClick={() => navigate(`/imaging/orders/${order.id}`)} sx={{ color: IMG_PRIMARY, fontWeight: 600 }}>
            Volver
          </Button>
          <BrokenImageIcon sx={{ color: IMG_PRIMARY, fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700} color={IMG_PRIMARY}>Informe de Imagen</Typography>
            <Typography variant="caption" color="text.secondary">{order.orderNumber || '—'}</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isApproved && (
            <Chip
              size="small"
              color="success"
              icon={<CheckCircleIcon />}
              label="Informe aprobado y firmado"
              sx={{ fontWeight: 600 }}
            />
          )}
          <Button variant="outlined" startIcon={<SaveIcon />} onClick={handleSaveDraft} disabled={saving || isApproved}>
            Guardar Borrador
          </Button>
          <Button variant="contained" startIcon={<CheckCircleIcon />} onClick={handleApprove} disabled={saving || isApproved}
            sx={{ bgcolor: SUCCESS, '&:hover': { bgcolor: '#059669' }, fontWeight: 600 }}>
            {isApproved ? 'Informado' : 'Informar y Firmar'}
          </Button>
        </Box>
      </Box>

      {/* Estudio: imágenes en resolución original */}
      <Paper elevation={0} sx={{ mb: 2, p: 2, borderRadius: 3, border: '1px solid #E2E8F0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={700} color={IMG_PRIMARY}>
            <ImageIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
            Imágenes del estudio ({order.files?.length || 0})
          </Typography>
          {(order.files || []).some((f) => (f.fileType || '').startsWith('image/')) && (
            <Button size="small" startIcon={<ZoomInIcon />}
              onClick={() => { setViewerIndex(0); setViewerOpen(true); }}
              sx={{ color: IMG_TEAL, fontWeight: 600 }}>
              Abrir visor HD
            </Button>
          )}
        </Box>

        {(order.files || []).length === 0 ? (
          <Alert severity="warning" sx={{ py: 0.5 }}>
            Esta orden no tiene imágenes cargadas todavía.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1 }}>
            {order.files.map((file, idx) => (
              (file.fileType || '').startsWith('image/') ? (
                <Box key={file.id || idx}
                  onClick={() => { setViewerIndex(idx); setViewerOpen(true); }}
                  sx={{
                    position: 'relative', width: 132, height: 132, flexShrink: 0,
                    borderRadius: 2, overflow: 'hidden', cursor: 'zoom-in',
                    border: '1px solid #E2E8F0', bgcolor: '#0B0F14',
                    '&:hover .zoom-hint': { opacity: 1 },
                  }}>
                  <img src={file.downloadURL} alt={file.fileName || ''}
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  <Box className="zoom-hint" sx={{
                    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', bgcolor: 'rgba(11,15,20,0.55)', color: '#fff',
                    opacity: 0, transition: 'opacity 0.15s',
                  }}>
                    <ZoomInIcon />
                  </Box>
                </Box>
              ) : (
                <Box key={file.id || idx} component="a" href={file.downloadURL}
                  target="_blank" rel="noopener"
                  sx={{
                    width: 132, height: 132, flexShrink: 0, borderRadius: 2,
                    border: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', gap: 0.5,
                    textDecoration: 'none', color: 'text.secondary', bgcolor: '#F8FAFC',
                  }}>
                  <PictureAsPdfIcon />
                  <Typography variant="caption" align="center" sx={{ px: 1 }} noWrap>
                    {file.fileName || 'Archivo'}
                  </Typography>
                </Box>
              )
            ))}
          </Box>
        )}
      </Paper>

      {/* Findings editor */}
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E2E8F0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} color={IMG_PRIMARY}>
            Informes radiológicos adjuntos
          </Typography>
          <Button
            size="small"
            variant="outlined"
            component="label"
            startIcon={uploadingReport ? <CircularProgress size={16} /> : <CloudUploadIcon />}
            disabled={uploadingReport}
          >
            Agregar PDF
            <input type="file" accept="application/pdf" hidden onChange={handleReportUpload} />
          </Button>
        </Box>

        {reportFiles.length > 0 ? reportFiles.map((file, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, bgcolor: '#F0FDF4', borderRadius: 2, mb: 1 }}>
            <PictureAsPdfIcon color="success" />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>{file.fileName}</Typography>
              <Typography variant="caption" color="text.secondary" display="block" noWrap>
                {new Date(file.uploadedAt).toLocaleString('es-CL')}
              </Typography>
            </Box>
            <Button size="small" href={file.downloadURL} target="_blank" rel="noopener" startIcon={<PictureAsPdfIcon />}>
              Ver
            </Button>
            {!isApproved && (
              <IconButton size="small" onClick={() => setReportFiles(prev => prev.filter((_, idx) => idx !== i))} sx={{ color: DANGER }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        )) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            No hay informes PDF cargados. Puedes adjuntar archivos generados externamente.
          </Alert>
        )}

        {findings.map((finding, idx) => (
          <Accordion key={finding.examTypeId} defaultExpanded sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }}/>}
              sx={{ bgcolor: IMG_PRIMARY, borderRadius: '12px 12px 0 0 !important' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip label={finding.modality} size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontSize: '0.65rem' }}/>
                <Typography variant="body2" sx={{ color: 'white', fontWeight: 700 }}>
                  {finding.examTypeName}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField label="Técnica" multiline rows={2}
                  value={finding.technique} onChange={(e) => updateFinding(idx, 'technique', e.target.value)}
                  helperText="Describir la técnica utilizada" fullWidth size="small" />
                <TextField label="Hallazgos *" multiline rows={5} required
                  value={finding.findings} onChange={(e) => updateFinding(idx, 'findings', e.target.value)}
                  helperText="Descripción detallada de los hallazgos" fullWidth size="small" />
                <TextField label="Impresión diagnóstica *" multiline rows={3} required
                  value={finding.impression} onChange={(e) => updateFinding(idx, 'impression', e.target.value)} fullWidth size="small" />
                <TextField label="Recomendaciones" multiline rows={2}
                  value={finding.recommendation} onChange={(e) => updateFinding(idx, 'recommendation', e.target.value)} fullWidth size="small" />

                {/* Measurements for ECO */}
                {finding.modality === 'ECO' && (
                  <Box>
                    <Typography variant="caption" fontWeight={700} display="block" mb={1}>
                      Mediciones
                    </Typography>
                    {finding.measurements?.map((m, mIdx) => (
                      <Box key={mIdx} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                        <TextField size="small" label="Estructura" value={m.structure}
                          onChange={(e) => updateMeasurement(idx, mIdx, 'structure', e.target.value)}
                          sx={{ flex: 2 }} />
                        <TextField size="small" label="Medida" value={m.value}
                          onChange={(e) => updateMeasurement(idx, mIdx, 'value', e.target.value)}
                          sx={{ flex: 1 }} placeholder="3.2 x 2.1 cm" />
                        <FormControlLabel
                          control={<Switch size="small" checked={m.normal}
                            onChange={(e) => updateMeasurement(idx, mIdx, 'normal', e.target.checked)}/>}
                          label={<Typography variant="caption">{m.normal ? 'Normal' : 'Anormal'}</Typography>}/>
                        <IconButton size="small" onClick={() => removeMeasurement(idx, mIdx)}>
                          <DeleteIcon fontSize="small"/>
                        </IconButton>
                      </Box>
                    ))}
                    <Button size="small" startIcon={<AddIcon/>} onClick={() => addMeasurement(idx)}>
                      Agregar medición
                    </Button>
                  </Box>
                )}

                {/* Critical finding */}
                <FormControlLabel
                  control={<Switch checked={finding.criticalFinding}
                    onChange={(e) => updateFinding(idx, 'criticalFinding', e.target.checked)}
                    color="error"/>}
                  label={<Typography variant="body2" color={finding.criticalFinding ? 'error' : 'text.secondary'}
                    fontWeight={finding.criticalFinding ? 700 : 400}>
                    Hallazgo crítico (requiere notificación inmediata)
                  </Typography>}
                />
                {finding.criticalFinding && (
                  <TextField label="Descripción del hallazgo crítico" multiline rows={2}
                    value={finding.criticalFindingText} required
                    onChange={(e) => updateFinding(idx, 'criticalFindingText', e.target.value)}
                    sx={{ bgcolor: '#FFF5F5', '& .MuiOutlinedInput-root': { borderColor: DANGER } }} fullWidth size="small" />
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}

        {/* Overall impression */}
        {findings.length > 1 && (
          <TextField fullWidth multiline rows={3}
            label="Impresión general de todos los estudios"
            value={overallImpression} onChange={(e) => setOverallImpression(e.target.value)}
            sx={{ mt: 2 }} size="small" />
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <ImageViewerDialog open={viewerOpen} onClose={() => setViewerOpen(false)}
        files={order.files || []} initialIndex={viewerIndex} />
    </Box>
  );
}
